import ViewerIcon from 'assets/images/others/3d-viewer.svg';
import ComposerIcon from 'assets/images/others/product-configurator.svg';
import CustomizerIcon from 'assets/images/others/visual-customizer.svg';
import { truncate } from 'lodash';
import React, { FC, Suspense, useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { fetchQuery, graphql, useLazyLoadQuery, useMutation, usePaginationFragment, useRefetchableFragment, useRelayEnvironment } from 'react-relay';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ConnectModelDialog from 'src/components/pages/productConfiguration/steps/6-done/connectModelDialog';
import DuplicateDialog from 'src/components/pages/productConfiguration/steps/6-done/duplicateDialog';
import DuplicateOptionsDialog from 'src/components/pages/productConfiguration/steps/6-done/duplicateOptionsDialog';
import { Badge } from 'src/components/widgets/badge';
import Loader from 'src/components/widgets/loader';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { useLastUsedTool } from 'src/shared/hooks';
import { BulkButton, BulkSelectionBar } from '../../widgets/bulkSelection';
import { Link, TextButton } from '../../widgets/buttons';
import { MessageDialog, QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { EmptyList } from '../../widgets/emptyList';
import { Icon } from '../../widgets/icon';
import { removeNotification, showErrorNotification, showInfoNotification, showNotification, showSuccessNotification } from '../../widgets/notificationToaster';
import {
    SHOPIFY_DUPLICATION_WARNING_DIALOG_DISMISSED_KEY,
    ShopifyDuplicationWarningDialog,
    shouldShowShopifyDuplicationWarningForPublish
} from '../../widgets/shopifyDuplicationWarning/shopifyDuplicationWarning';
import { Spinner } from '../../widgets/spinner';
import { LinkCell, Table, TableHeading, useSelectableTable } from '../../widgets/table';
import { ZTooltip } from '../../widgets/ztooltip';

import NoImage from 'assets/images/others/no-image.png';
import config from 'src/shared/config';
import { NoIntegrationDialog, useUpgradeDialogs } from 'src/shared/dialogs';
import { useFeatureFlag, useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { T, backofficeUrl, isDraftProduct, stringFormat, toGlobalID, toLocalID, uuid } from 'src/shared/helpers';
import { EnvType, useInfiniteScroll } from 'src/shared/hooks';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import useNotFirstRenderEffect from 'src/shared/hooks/useNotFirstRender';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useProductIsResizable } from 'src/shared/hooks/useProductIsResizable';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import EyeSlash from 'src/shared/material_icons/action/visibility_off.svg';
import Eye from 'src/shared/material_icons/action/visibility.svg';
import NotFoundIcon from 'src/shared/material_icons/image/broken_image.svg';
import SyncIcon from 'src/shared/material_icons/notification/sync.svg';
import Trash from 'src/shared/material_icons/action/delete.svg';
import { WixPublishErrorMessage } from 'src/shared/messages';
import AngleDown from 'src/shared/svg/angleDown';
import styled from 'styled-components';
import { DropdownContent, DropdownDivider, DropdownItemBody, DropdownItemText, DropdownList, DropdownListItem, useDropdown } from '../../widgets/dropdown';
import { DialogOverlay } from '../dashboard/intro/dialogDel';
import { useProductConfigurationState } from '../productConfiguration/state';
import { ProductColor } from '../productConfiguration/state/model/modelColor';
import AddSalesChannelDialog from '../salesChannels/addSalesChannelDialog';
import { generateStoreNumbers } from '../salesChannels/helpers';
import { SalesChannelNotAvailableDialogWindow } from '../salesChannels/salesChannel';
import { SubscriptionFragmentMaxProductsRefetchQuery } from './__generated__/SubscriptionFragmentMaxProductsRefetchQuery.graphql';
import { modelListProductsPaginationQuery } from './__generated__/modelListProductsPaginationQuery.graphql';
import { productDuplicationTaskQueriesCreateMutation } from './__generated__/productDuplicationTaskQueriesCreateMutation.graphql';
import { productListConnectModelMutation, productListConnectModelMutation$data } from './__generated__/productListConnectModelMutation.graphql';
import { productListDeleteMutation } from './__generated__/productListDeleteMutation.graphql';
import { productListProductSetStatusMutation } from './__generated__/productListProductSetStatusMutation.graphql';
import { productListProductsDeleteMutation } from './__generated__/productListProductsDeleteMutation.graphql';
import { ProductStatus, productListProductsSetStatusMutation } from './__generated__/productListProductsSetStatusMutation.graphql';
import { productListShopifyCustomizationStrategyQuery } from './__generated__/productListShopifyCustomizationStrategyQuery.graphql';
import { productListSubscriptionQuery } from './__generated__/productListSubscriptionQuery.graphql';
import { productListSyncWithEcommerceMutation } from './__generated__/productListSyncWithEcommerceMutation.graphql';
import { ProductProvider, ZakekeEnvironment, productList_models$data, productList_models$key } from './__generated__/productList_models.graphql';
import { productList_subscriptionFragmentMaxProducts$key } from './__generated__/productList_subscriptionFragmentMaxProducts.graphql';
import { productsQuery } from './__generated__/productsQuery.graphql';
import MarketplacePreviewUrlDialog from './marketplacePreviewUrlDialog';
import { createProductDuplicationTaskMutation } from './productDuplicationTaskQueries';
import { ProductTableRowsSkeleton, ProductTableSkeleton } from './productsSkeleton';
import ProductSalesChannelList, { DisconnectProductSalesChannel } from './productSalesChannelList';
import ProductSidePanel from './productSidebar';
import { DISABLED_PROVIDERS, productProviderIconsList, productProviderNameList } from './utils';
import MaterialMoreVertSvg from 'src/shared/material_icons/navigation/more_vert.svg';
import MaterialChevronRightSvg from 'src/shared/material_icons/navigation/chevron_right.svg';
import MaterialStorefrontSvg from 'src/shared/material_icons/places/storefront.svg';
import MaterialVisibilitySvg from 'src/shared/material_icons/action/visibility.svg';
export type Model = Exclude<Exclude<productList_models$data['models'], null>['edges'], null>[0]['node'];
export type AdditionalCodes = Exclude<Model, null>['additionalCodes'];
export type ProductSalesChannel = Exclude<Model, null>['salesChannel'];
type PrintingMethods = Exclude<Model, null>['printingMethods'];
type StandaloneSite = productsQuery['response']['standaloneSites'][number];

interface Props {
    query: productList_models$key;
    subscriptionFragment: productList_subscriptionFragmentMaxProducts$key;
    salesChannels: productsQuery['response']['salesChannels'];
    standaloneSites: productsQuery['response']['standaloneSites'];
    search: string;
    count: number;
    salesChannelsIDs?: string[];
    productProvidersIDs?: string[];
    productStatusID?: number | null | undefined;
    ecommerceName?: string;
    handleEmptyList: (isEmpty: boolean) => void;
    orderBy: string;
    onSalesChannelCreated: () => void;
    isShopifyTestStore: boolean;
    environmentSelected: number[];
    userID: number;
    disableProviderProducts?: boolean;
    onDuplicationTaskCreated?: () => void;
}

interface Store {
    id: string;
    storeName: string | null | undefined;
    storeNumber: number;
    storeUrl?: string | null | undefined;
}

interface NameCellProps {
    name: string;
    id: string; // modelID
    env: ZakekeEnvironment;
    onNameClick: () => void;
}

interface ProductProviderIconProps {
    productProvider: ProductProvider | 'TEMPLATE';
    sourceStores?: Store[];
    salesChannelID?: string;
}

const ProductImage = styled.img`
    border-radius: 4px;
    background: #f3f4f6;
    object-fit: cover;
`;

const CodeCell = styled.div`
    font-size: 13px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

// Product code cell: wraps onto up to two lines, truncates the second line with an ellipsis,
// and shows the full value in a tooltip only when the text is actually truncated.
const ProductCodeCell: FC<{ code: string; modelID: number }> = ({ code, modelID }) => {
    const { setRef, isTruncated } = useIsTruncated();
    return (
        <ZTooltip position='top' title={isTruncated ? T._(code, 'Admin') : undefined} interactive={true} addContainer>
            <CodeCell ref={setRef} data-testid={`product-code-${modelID}`}>
                {code}
            </CodeCell>
        </ZTooltip>
    );
};

const StyledSpinner = styled(Loader)`
    height: 30px;
`;

const ContainerProviderIcon = styled.div`
    display: flex;
    flex-direction: row;
    align-content: center;
`;

const StyledSpinnerProviderSync = styled(Spinner)`
    height: 20px;
    align-self: center;
`;

const LeftColContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-left: 1rem;
`;

const RightColContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
    padding-right: 0.5rem;
`;

const ActionsColContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 2px;
    width: 100%;
`;

const RowActionBtn = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 4px;
    border: none;
    background: none;
    color: #263238;
    cursor: pointer;
    transition: all 0.15s;

    &:hover {
        background: #f3f4f6;
        color: #263238;
    }
`;

const ProductRowActionIcon = styled(Icon)``;

const ProductActionsMenuContainer = styled.div`
    display: flex;
    align-items: center;
`;

const SalesChannelColContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 100%;
    margin-left: 1rem;
    margin-right: 1rem;
    height: 26px;
    line-height: 26px;
    text-align: center;
`;

const PrintingMethodsColContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 100%;
    margin-left: 1rem;
    margin-right: 1rem;
`;

const PrintingMethodsListContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;

    & > * {
        margin-left: 0.2rem;
        margin-right: 0.2rem;
        font-size: 14px;
    }
`;

const PrintingMethodsListPopUpContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    justify-content: flex-start;

    & > * {
        margin-left: 0.2rem;
        margin-right: 0.2rem;
        margin-top: 2px;
        margin-bottom: 2px;
        font-size: 14px;
    }
`;

const SalesChannelsTooltipContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 240px;
    max-width: 360px;
    text-align: left;
`;

const SalesChannelTooltipItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

const SalesChannelTooltipName = styled.span`
    font-weight: 600;
    color: ${(props) => props.theme.app.primary};
`;

const SalesChannelTooltipCode = styled.span`
    font-size: 12px;
    color: #78909c;
`;

const NumberAdditionalSC = styled.div`
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    color: ${(props) => props.theme.button.color};
    margin-left: 0.2rem;
    font-weight: bold;
    white-space: nowrap;
    font-family: ${(props) => props.theme.app.font};
    font-size: 14px;
`;

export const StyledBadge = styled(Badge)`
    font-weight: 600;
    width: unset;
    white-space: nowrap;
`;

export const DialogOverlayConnect = styled(DialogOverlay)`
    z-index: 9999;
    justify-content: center;
`;

const SalesChannelClickable = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    border-radius: 4px;
    padding: 2px 6px;

    &:hover {
        background-color: ${(props) => props.theme.skeleton.backgroundColor};
    }
`;

const WrapperAngleDown = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

const TableToolIcon = styled.span`
    display: inline-flex;
    width: 24px;
    height: 24px;
    border-radius: 4px;

    svg {
        width: 100%;
        height: 100%;
    }
`;

const SourceCell = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: #263238;
`;

const BrokenBadge = styled(Badge)`
    margin-left: 8px;
`;

const TooltipSourceContent = styled.div`
    margin: 0;
    p {
        margin-top: 0;
    }
`;

// Product name link: wraps onto up to two lines, then truncates the second line with an ellipsis.
const ClampedNameLink = styled.a`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

// Attaches to a clamped element and reports whether its content is actually truncated
// (overflowing the clamp), so the tooltip is shown only when it is really needed.
const useIsTruncated = () => {
    const [isTruncated, setIsTruncated] = useState(false);
    const observerRef = useRef<ResizeObserver | null>(null);
    const setRef = useCallback((el: HTMLElement | null) => {
        observerRef.current?.disconnect();
        if (!el) {
            observerRef.current = null;
            return;
        }
        const check = () => setIsTruncated(el.scrollHeight - el.clientHeight > 1 || el.scrollWidth - el.clientWidth > 1);
        check();
        observerRef.current = new ResizeObserver(check);
        observerRef.current.observe(el);
    }, []);
    return { setRef, isTruncated };
};

const NameCell = (props: NameCellProps) => {
    const { setRef, isTruncated } = useIsTruncated();
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        props.onNameClick();
    };
    return (
        <LinkCell data-testid={`product-name-cell-${props.id}`}>
            <ZTooltip position='top' title={isTruncated ? T._(props.name, 'Admin') : undefined} interactive={true} addContainer>
                <ClampedNameLink
                    ref={setRef}
                    onClick={handleClick}
                    role='button'
                    aria-label={`Edit product: ${props.name}`}
                    data-testid={`product-name-link-${props.id}`}
                >
                    {props.name}
                </ClampedNameLink>
            </ZTooltip>
        </LinkCell>
    );
};

const hasBrokenSalesChannel = (product: Model) => {
    return !!product.salesChannel && product.salesChannel.ecommerceSettings === null;
};

const getSourceStores = (salesChannels: productsQuery['response']['salesChannels']) => {
    const sourceStores: Store[] = [];

    const salesChannelIDs = salesChannels.filter((x) => x.ecommerceSettings?.ecommerceName !== 'NONE').map((channel) => channel.id || '');

    const storeNumbers = generateStoreNumbers(salesChannelIDs);

    storeNumbers.forEach((store) => {
        const salesChannel = salesChannels.find((x) => x.id === store.salesChannelID);
        if (salesChannel) {
            sourceStores.push({
                id: salesChannel.id,
                storeName: salesChannel.detail?.properties?.name,
                storeNumber: store.storeNumber,
                storeUrl: salesChannel.ecommerceSettings?.definitiveUrl
            });
        }
    });

    return sourceStores;
};

const isMarketplaceProduct = (product: Model) => {
    return (
        product.salesChannel.ecommerceSettings?.ecommerceName === 'ETSY' ||
        product.additionalCodes.some((c) => c.salesChannel.ecommerceSettings?.ecommerceName === 'ETSY')
    );
};

const ProductProviderIcon = (props: ProductProviderIconProps) => {
    const sourceStore = props.salesChannelID && props.sourceStores ? props.sourceStores.find((x) => x.id === props.salesChannelID) : undefined;

    return (
        <ZTooltip
            position='top'
            interactive={true}
            html={
                <>
                    {sourceStore ? (
                        <TooltipSourceContent>
                            <p>{sourceStore.storeName || T._(`Store ${sourceStore.storeNumber}`, 'Admin')}</p>
                            <Link target='_blank' href={sourceStore.storeUrl || ''}>
                                {sourceStore.storeUrl}
                            </Link>
                        </TooltipSourceContent>
                    ) : (
                        <>{T._(productProviderNameList[props.productProvider as keyof typeof productProviderNameList] || props.productProvider, 'Admin')}</>
                    )}
                </>
            }
        >
            <Icon>{productProviderIconsList[props.productProvider as keyof typeof productProviderIconsList] || productProviderIconsList.UNDEFINED}</Icon>
        </ZTooltip>
    );
};

const Image = (props: { url?: string }) => {
    return <ProductImage src={props.url || NoImage} width='40' height='40' data-testid='product-image' />;
};

const subscriptionQuery = graphql`
    query productListSubscriptionQuery {
        subscription {
            maxProducts {
                numPublishedProducts
                maxProducts
            }
        }
    }
`;

export const productsQueryConnection = graphql`
    fragment productList_models on Query
    @refetchable(queryName: "modelListProductsPaginationQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 20 }
        cursor: { type: "String", defaultValue: "1" }
        search: { type: "String", defaultValue: "" }
        orderBy: { type: "String", defaultValue: "" }
        salesChannelsIDs: { type: "[String!]", defaultValue: null }
        productProvidersIDs: { type: "[String!]", defaultValue: null }
        productStatusID: { type: "Int", defaultValue: null }
    ) {
        models(
            first: $count
            after: $cursor
            search: $search
            orderBy: $orderBy
            salesChannelsIDs: $salesChannelsIDs
            productProvidersIDs: $productProvidersIDs
            productStatusID: $productStatusID
        ) @connection(key: "productsModels_models") {
            __id # This is the cache id
            edges {
                node {
                    ... on Model {
                        id
                        name
                        modelID
                        code
                        providerCode
                        provider
                        productProviderSyncState
                        statusID
                        configurationStatus
                        smallImageUrl
                        zakekeEnvironment
                        canAddAdditionalCode
                        sourceProductTemplateID
                        additionalCodes {
                            code
                            salesChannelID
                            merchantProductName
                            salesChannel {
                                id
                                detail {
                                    id
                                    properties {
                                        name
                                    }
                                }
                                ecommerceSettings {
                                    ecommerceName
                                    remoteUrl
                                    productsApi
                                    hasProductsApi
                                    definitiveUrl
                                }
                            }
                        }
                        modelColorMappings {
                            colorID
                            salesChannelID
                            merchantCode
                        }
                        salesChannel {
                            id
                            detail {
                                id
                                properties {
                                    name
                                }
                            }
                            ecommerceSettings {
                                ecommerceName
                                remoteUrl
                                productsApi
                                hasProductsApi
                                definitiveUrl
                            }
                        }
                        printingMethods {
                            printTypeID
                            name
                        }
                        colors {
                            colorID
                            name
                            code
                            colorCode
                        }
                    }
                }
            }
        }
    }
`;

const deleteMutation = graphql`
    mutation productListDeleteMutation($connections: [ID!]!, $productID: String!) {
        deleteProduct(productID: $productID) {
            id @deleteEdge(connections: $connections)
        }
    }
`;

const setStatusMassiveMutation = graphql`
    mutation productListProductsSetStatusMutation($productIDs: [String!]!, $status: ProductStatus!) {
        setStatusProducts(productIDs: $productIDs, status: $status) {
            id
            statusID
        }
    }
`;

const deleteMassiveMutation = graphql`
    mutation productListProductsDeleteMutation($connections: [ID!]!, $productIDs: [String!]!) {
        deleteProducts(productIDs: $productIDs) {
            id @deleteEdge(connections: $connections)
        }
    }
`;

const setStatusMutation = graphql`
    mutation productListProductSetStatusMutation($input: SetStatusProductInput!) {
        setStatusProduct(input: $input) {
            model {
                id
                statusID
            }
            errors {
                ... on Error {
                    message
                }
            }
        }
    }
`;

const subscriptionFragmentMaxProducts = graphql`
    fragment productList_subscriptionFragmentMaxProducts on Query @refetchable(queryName: "SubscriptionFragmentMaxProductsRefetchQuery") {
        subscription {
            id
            areSalesChannelsEnabled
            maxProducts {
                pricingVersion
                canAddProduct
                maxProducts
                numProducts
                numPublishedProducts
            }
        }
    }
`;

const syncWithEcommerceMutation = graphql`
    mutation productListSyncWithEcommerceMutation($productIDs: [String!]!) {
        syncWithEcommerce(productIDs: $productIDs) {
            id
            name
            smallImageUrl
        }
    }
`;

const shopifyCustomizationStrategyQuery = graphql`
    query productListShopifyCustomizationStrategyQuery {
        ecommerceSettingsAdditionalDataForEcommercePlatform(ecommerce: SHOPIFY) {
            customizationStrategy
        }
    }
`;

const PrintingMethodsList: FC<{ printingMethods: PrintingMethods }> = ({ printingMethods }) => {
    const maxLength = window.innerWidth < 1900 ? 15 : 20;

    return (
        <PrintingMethodsListContainer>
            {printingMethods.map((printingMethod, i) => {
                return i < 1 && (printingMethod.name.length > maxLength || printingMethods.length > 1) ? (
                    <ZTooltip
                        key={i}
                        html={<PrintingMethodsListPopUp printingMethods={printingMethods}></PrintingMethodsListPopUp>}
                        position={'top'}
                        interactive={true}
                    >
                        <p>{truncate(printingMethod.name, { length: maxLength })}</p>
                    </ZTooltip>
                ) : (
                    <React.Fragment key={i}> {i < 1 && <p>{printingMethod.name}</p>} </React.Fragment>
                );
            })}
            {printingMethods.length > 1 && (
                <>
                    <NumberAdditionalSC>+ {printingMethods.length - 1}</NumberAdditionalSC>
                </>
            )}
        </PrintingMethodsListContainer>
    );
};

const PrintingMethodsListPopUp: FC<{ printingMethods: PrintingMethods }> = ({ printingMethods }) => {
    return (
        <PrintingMethodsListPopUpContainer>
            {printingMethods.map((printingMethod, i) => {
                return <p key={i}>{T._(printingMethod.name, 'Admin')}</p>;
            })}
        </PrintingMethodsListPopUpContainer>
    );
};

interface SalesChannelTooltipItemModel {
    id: string;
    name: string;
    productCode: string;
    url: string | null | undefined;
}

const getSalesChannelTooltipItems = (product: Model): SalesChannelTooltipItemModel[] => {
    const channels: SalesChannelTooltipItemModel[] = [];

    if (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
        channels.push({
            id: product.salesChannel.id,
            name: product.salesChannel.detail?.properties?.name || T._('Sales channel', 'Admin'),
            productCode: product.code,
            url: product.salesChannel.ecommerceSettings?.definitiveUrl || product.salesChannel.ecommerceSettings?.remoteUrl
        });
    }

    product.additionalCodes.forEach((additionalCode) => {
        if (additionalCode.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') {
            channels.push({
                id: additionalCode.salesChannel.id,
                name: additionalCode.salesChannel.detail?.properties?.name || T._('Sales channel', 'Admin'),
                productCode: additionalCode.code,
                url: additionalCode.salesChannel.ecommerceSettings?.definitiveUrl || additionalCode.salesChannel.ecommerceSettings?.remoteUrl
            });
        }
    });

    return channels;
};

const SalesChannelsTooltipContent: FC<{ channels: SalesChannelTooltipItemModel[] }> = ({ channels }) => {
    return (
        <SalesChannelsTooltipContainer>
            {channels.map((channel) => (
                <SalesChannelTooltipItem key={channel.id}>
                    <SalesChannelTooltipName>{channel.name}</SalesChannelTooltipName>
                    <SalesChannelTooltipCode>
                        {T._('Code', 'Admin')}: {channel.productCode}
                    </SalesChannelTooltipCode>
                    {channel.url ? (
                        <Link href={channel.url} target='_blank' onClick={(e) => e.stopPropagation()}>
                            {channel.url}
                        </Link>
                    ) : (
                        <span>{T._('No URL available', 'Admin')}</span>
                    )}
                </SalesChannelTooltipItem>
            ))}
        </SalesChannelsTooltipContainer>
    );
};

const SalesChannelCountTooltip: FC<{ channels: SalesChannelTooltipItemModel[]; children: React.ReactElement }> = ({ channels, children }) => {
    if (channels.length === 0) return children;

    return (
        <ZTooltip position='top' interactive={true} html={<SalesChannelsTooltipContent channels={channels} />}>
            {children}
        </ZTooltip>
    );
};

interface ProductActionMenuItem {
    key: string;
    label: string;
    onClick: () => void | Promise<void>;
    disabled?: boolean;
    danger?: boolean;
    testId?: string;
}

interface ProductActionsDropdownProps {
    product: Model;
    salesChannels: Props['salesChannels'];
    isNewConfiguratorBackoffice: boolean;
    isInFightUpdateMassive: boolean;
    isFetchingUpdatedPublishedProductsList: boolean;
    canPublishToStandaloneSite: boolean;
    canAddSalesChannelFromPublishedMiniSite: boolean;
    onShowDetails: (productID: string) => void;
    onEdit: (product: Model) => void | Promise<void>;
    onSetStatus: (product: Model) => void;
    onPublishToStandaloneSite: (product: Model) => void;
    onDuplicate: (product: Model) => void;
    onAddSalesChannels: (product: Model) => void;
    onDisconnectSalesChannel: (product: Model) => void;
    onUpdateVariants: (product: Model) => void | Promise<void>;
    onSyncWithEcommerce: (productIDs: string[]) => void;
    onMarketplacePreview: (productID: string) => void;
    onDelete: (product: Model) => void;
}

const ProductActionsDropdown: FC<ProductActionsDropdownProps> = ({
    product,
    salesChannels,
    isNewConfiguratorBackoffice,
    isInFightUpdateMassive,
    isFetchingUpdatedPublishedProductsList,
    canPublishToStandaloneSite,
    canAddSalesChannelFromPublishedMiniSite,
    onShowDetails,
    onEdit,
    onSetStatus,
    onPublishToStandaloneSite,
    onDuplicate,
    onAddSalesChannels,
    onDisconnectSalesChannel,
    onUpdateVariants,
    onSyncWithEcommerce,
    onMarketplacePreview,
    onDelete
}) => {
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();
    const hasOnlineSalesChannels = salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');
    const canAddToSalesChannel =
        product.canAddAdditionalCode &&
        (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || product.additionalCodes.length >= 1 || canAddSalesChannelFromPublishedMiniSite);

    const primaryActions: ProductActionMenuItem[] = [
        {
            key: 'details',
            label: T._('Product details', 'Admin'),
            onClick: () => onShowDetails(product.id),
            testId: `details-button-${product.modelID}`
        },
        {
            key: 'edit',
            label: T._('Edit product', 'Admin'),
            onClick: () => onEdit(product),
            testId: `edit-button-${product.modelID}`
        }
    ];

    if (product.zakekeEnvironment !== 'VIEWER') {
        const setStatusAction =
            canPublishToStandaloneSite && product.statusID !== 1 && !hasOnlineSalesChannels
                ? () => onPublishToStandaloneSite(product)
                : () => onSetStatus(product);

        primaryActions.push({
            key: 'setstatus',
            label:
                product.statusID === 1
                    ? T._('Unpublish', 'Admin')
                    : canPublishToStandaloneSite && !hasOnlineSalesChannels
                      ? T._('Add to mini-site', 'Admin')
                      : canPublishToStandaloneSite
                        ? T._('Add to an online store', 'Admin')
                        : !hasOnlineSalesChannels
                          ? T._('Connect and publish', 'Admin')
                          : T._('Publish', 'Admin'),
            disabled: isInFightUpdateMassive || isFetchingUpdatedPublishedProductsList,
            onClick: setStatusAction,
            testId: `publish-unpublish-button-${product.modelID}`
        });
    }

    if (canPublishToStandaloneSite && product.statusID !== 1 && hasOnlineSalesChannels) {
        primaryActions.push({
            key: 'publishStandaloneSite',
            label: T._('Add to mini-site', 'Admin'),
            disabled: isInFightUpdateMassive || isFetchingUpdatedPublishedProductsList,
            onClick: () => onPublishToStandaloneSite(product),
            testId: `publish-mini-site-button-${product.modelID}`
        });
    }

    if (
        product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' &&
        product.zakekeEnvironment !== 'VIEWER' &&
        product.provider !== 'INVITION' &&
        product.provider !== 'CUSTOM_CAT' &&
        product.provider !== 'PRINTFUL' &&
        (product.zakekeEnvironment === 'CUSTOMIZER' || (product.zakekeEnvironment === 'COMPOSER' && isNewConfiguratorBackoffice))
    ) {
        primaryActions.push({
            key: 'duplicate',
            label: T._('Duplicate', 'Admin'),
            onClick: () => onDuplicate(product),
            testId: `duplicate-button-${product.modelID}`
        });
    }

    if (canAddToSalesChannel) {
        primaryActions.push({
            key: 'addChannel',
            label: T._('Add to an Online Store', 'Admin'),
            onClick: () => onAddSalesChannels(product),
            testId: `add-sales-channel-button-${product.modelID}`
        });
    }

    if (product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' && product.additionalCodes.length === 1) {
        primaryActions.push({
            key: 'disconnectSalesChannel',
            label: T._('Disconnect from Sales Channel', 'Admin'),
            onClick: () => onDisconnectSalesChannel(product),
            testId: `disconnect-sales-channel-button-${product.modelID}`
        });
    }

    if (product.zakekeEnvironment === 'CUSTOMIZER' && product.provider !== 'INVITION' && product.provider !== 'CUSTOM_CAT' && product.provider !== 'PRINTFUL') {
        primaryActions.push({
            key: 'updateVariations',
            label: T._('Add or update variants', 'Admin'),
            onClick: () => onUpdateVariants(product),
            testId: `update-variants-button-${product.modelID}`
        });
    }

    if (
        (product.zakekeEnvironment === 'COMPOSER' || product.zakekeEnvironment === 'CUSTOMIZER') &&
        (product.salesChannel?.ecommerceSettings?.ecommerceName === 'SHOPIFY' ||
            product.salesChannel?.ecommerceSettings?.ecommerceName === 'WOO_COMMERCE' ||
            product.salesChannel?.ecommerceSettings?.ecommerceName === 'BIG_COMMERCE' ||
            product.salesChannel?.ecommerceSettings?.ecommerceName === 'MAGENTO' ||
            product.salesChannel?.ecommerceSettings?.ecommerceName === 'PRESTA_SHOP')
    ) {
        primaryActions.push({
            key: 'sync',
            label: T._('Sync image/name with e-commerce', 'Admin'),
            onClick: () => onSyncWithEcommerce([product.id]),
            testId: `sync-button-${product.modelID}`
        });
    }

    if (isMarketplaceProduct(product)) {
        primaryActions.push({
            key: 'marketplacePreview',
            label: T._('Generate a shareable demo product URL', 'Admin'),
            disabled: product.statusID !== 1,
            onClick: () => onMarketplacePreview(product.id),
            testId: `marketplace-preview-button-${product.modelID}`
        });
    }

    const dangerActions: ProductActionMenuItem[] =
        product.zakekeEnvironment !== 'VIEWER'
            ? [
                  {
                      key: 'delete',
                      label: T._('Delete', 'Admin'),
                      danger: true,
                      onClick: () => onDelete(product),
                      testId: `delete_button_${product.modelID}`
                  }
              ]
            : [];

    const sections = [primaryActions, dangerActions].filter((section) => section.length > 0);

    const handleActionClick = (action: ProductActionMenuItem) => {
        if (action.disabled) return;

        closeDropdown();
        void action.onClick();
    };

    return (
        <ProductActionsMenuContainer data-testid={`actions-menu-${product.modelID}`}>
            <RowActionBtn
                type='button'
                onClick={(e) => {
                    e.stopPropagation();
                    isOpen ? closeDropdown() : openDropdown(e.currentTarget, 'bottom-end');
                }}
                aria-label={T._('Product actions', 'Admin')}
                aria-haspopup='menu'
                aria-expanded={isOpen}
                data-testid={`actions-menu-icon-${product.modelID}`}
            >
                <ProductRowActionIcon aria-hidden='true'>
                    <MaterialMoreVertSvg />
                </ProductRowActionIcon>
            </RowActionBtn>
            {isOpen && (
                <Dropdown minWidth='300px' maxWidth='360px' surfacePadding='0' zIndex={1000}>
                    <DropdownContent onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <DropdownList role='menu'>
                            {sections.map((section, sectionIndex) => (
                                <React.Fragment key={`section_${sectionIndex}`}>
                                    {sectionIndex > 0 && <DropdownDivider />}
                                    {section.map((action) => (
                                        <DropdownListItem
                                            key={action.key}
                                            type='button'
                                            role='menuitem'
                                            disabled={action.disabled}
                                            $danger={action.danger}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleActionClick(action);
                                            }}
                                            data-testid={action.testId}
                                        >
                                            <DropdownItemBody>
                                                <DropdownItemText>{action.label}</DropdownItemText>
                                            </DropdownItemBody>
                                        </DropdownListItem>
                                    ))}
                                </React.Fragment>
                            ))}
                        </DropdownList>
                    </DropdownContent>
                </Dropdown>
            )}
        </ProductActionsMenuContainer>
    );
};

const ProductList: FC<Props> = ({
    query,
    subscriptionFragment,
    salesChannels,
    standaloneSites,
    handleEmptyList,
    search,
    orderBy,
    count,
    salesChannelsIDs,
    productProvidersIDs,
    productStatusID,
    ecommerceName,
    onSalesChannelCreated,
    isShopifyTestStore,
    environmentSelected,
    userID,
    disableProviderProducts = false,
    onDuplicationTaskCreated
}) => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { setLastUsedTool } = useLastUsedTool();
    const navigate = useNavigate();
    const { dispatch } = useProductConfigurationState();
    const { showPlanUpgradeDialog } = useUpgradeDialogs();
    const { sellerMarketplace } = useSubscriptionData();
    const { isAllowed } = usePermissions();
    const Environment = useRelayEnvironment();
    const isStandaloneEnabled = useFeatureFlag('standalone');

    const { data, loadNext, refetch, hasNext, isLoadingNext } = usePaginationFragment<modelListProductsPaginationQuery, productList_models$key>(
        productsQueryConnection,
        query
    );

    const isNewConfiguratorBackoffice = useFeatureFlagWithConditions('newConfiguratorBackoffice', {
        userId: userID.toString()
    });
    const isPrototypeModeEnabled = useFeatureFlagWithConditions('prototypeMode', {
        userId: userID.toString()
    });

    const environmentsSelected = environmentSelected.map((env) => {
        switch (env) {
            case 1:
                return 'CUSTOMIZER';
            case 2:
                return 'COMPOSER';
            case 3:
                return 'VIEWER';
            default:
                return 'UNKNOWN';
        }
    });

    const [subscriptionData, refetchSubscription] = useRefetchableFragment<
        SubscriptionFragmentMaxProductsRefetchQuery,
        productList_subscriptionFragmentMaxProducts$key
    >(subscriptionFragmentMaxProducts, subscriptionFragment);

    const [commitConnectModel, isInFightConnectModel] = useMutation<productListConnectModelMutation>(graphql`
        mutation productListConnectModelMutation($id: ID!, $target: ModelDuplicateTargetInput!) {
            connectModel(id: $id, target: $target)
        }
    `);

    const productList = (data?.models?.edges ?? []).map(({ node }) => node);
    const standaloneSitesForPublishing = isStandaloneEnabled ? standaloneSites : [];

    const getStandaloneSitesForProduct = (product: Model): StandaloneSite[] => {
        if (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') return [];
        return standaloneSitesForPublishing.filter((site) => site.salesChannelGlobalID === product.salesChannel.id);
    };

    const canPublishProductToStandaloneSite = (product: Model) => getStandaloneSitesForProduct(product).length > 0;
    const canAddSalesChannelFromPublishedMiniSite = (product: Model) =>
        product.statusID === 1 &&
        product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
        product.additionalCodes.length === 0 &&
        canPublishProductToStandaloneSite(product);

    const visibleProductList =
        environmentsSelected.length === 0 ? productList : productList.filter((product) => environmentsSelected.includes(product.zakekeEnvironment));

    const { showDialog, closeDialog } = useDialogManager();
    const { sellerStaticsDetail } = useAccountData();
    const shopifyCustomizationStrategyData = useLazyLoadQuery<productListShopifyCustomizationStrategyQuery>(shopifyCustomizationStrategyQuery, {});
    const { isResizable } = useProductIsResizable();
    const [isPending, startTransition] = useTransition();
    const [deleteProduct, isInFightDelete] = useMutation<productListDeleteMutation>(deleteMutation);
    const { selectedData, mapRows, headingCheck, resetSelection } = useSelectableTable(visibleProductList);
    const [setStatusProductMassive, isInFightUpdateMassive] = useMutation<productListProductsSetStatusMutation>(setStatusMassiveMutation);
    const [deleteProductMassive, isInFightDeleteMassive] = useMutation<productListProductsDeleteMutation>(deleteMassiveMutation);
    const [commitDuplicate, isInFightDuplicate] = useMutation<productDuplicationTaskQueriesCreateMutation>(createProductDuplicationTaskMutation);
    const [setStatusProduct, isInFightUpdate] = useMutation<productListProductSetStatusMutation>(setStatusMutation);
    const [syncWithEcommerce, isInFightSync] = useMutation<productListSyncWithEcommerceMutation>(syncWithEcommerceMutation);
    const [isFetchingUpdatedPublishedProductsList, setIsFetchingUpdatedPublishedProductsList] = useState(false);
    const [hasResumed, setHasResumed] = useState(false);
    const [selectedProductId, _setSelectedProductId] = useState<string | null>(() => {
        const sidebarParam = searchParams.get('sidebar');
        return sidebarParam ? toGlobalID(sidebarParam, 'Model') : null;
    });

    const maybeShowShopifyDuplicationWarning = (publishedShopifyProduct: boolean, isFirstPublishedProduct: boolean) => {
        if (!publishedShopifyProduct) return;
        if (!shouldShowShopifyDuplicationWarningForPublish(sellerStaticsDetail?.registeredOn, isFirstPublishedProduct)) return;
        if (localStorage.getItem(SHOPIFY_DUPLICATION_WARNING_DIALOG_DISMISSED_KEY) === 'true') return;

        const isDuplication = shopifyCustomizationStrategyData.ecommerceSettingsAdditionalDataForEcommercePlatform?.some(
            (settings) => settings.customizationStrategy === 'DUPLICATION'
        );
        if (!isDuplication) return;

        showDialog('shopify-duplication-warning', <ShopifyDuplicationWarningDialog />);
    };

    const setSelectedProductId = (id: string | null) => {
        _setSelectedProductId(id);
        setSearchParams(
            (prev) => {
                if (id) {
                    prev.set('sidebar', String(toLocalID(id)));
                } else {
                    prev.delete('sidebar');
                }
                return prev;
            },
            { replace: true }
        );
    };
    const getSelectedProduct = () => (selectedProductId ? (productList.find((p) => p.id === selectedProductId) ?? null) : null);

    useInfiniteScroll(() => loadNext(20), hasNext);

    useNotFirstRenderEffect(() => {
        startTransition(() => {
            refetchProducts();
        });
    }, [search, orderBy, salesChannelsIDs, productProvidersIDs, productStatusID]);

    // Re-open sidebar when navigating back from a tool page
    useEffect(() => {
        const { sidebarProductId } = (location.state as any) || {};
        if (sidebarProductId) {
            setSelectedProductId(sidebarProductId);
            navigate(location.pathname + location.search, { replace: true, state: {} });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const hasNotifiedBrokenRef = useRef(false);
    useEffect(() => {
        if (hasNotifiedBrokenRef.current) return;
        if (productList.some(hasBrokenSalesChannel)) {
            hasNotifiedBrokenRef.current = true;
            showErrorNotification({
                message: (
                    <>
                        {T._(
                            "Some products have been marked as 'Broken' in Customizable Products and are no longer functioning on the websites where they are published because the originating sales channel has been removed. Please contact ",
                            'Admin'
                        )}
                        <Link href='mailto:help@zakeke.com'>help@zakeke.com</Link>
                        {T._(' for assistance.', 'Admin')}
                    </>
                ),
                dismiss: { duration: 15000 }
            });
        }
    }, [productList]);

    //resume first publishing after integration
    useEffect(() => {
        if (hasResumed) return;

        const { doPublishImmediately, productID } = location.state || {};
        if (!doPublishImmediately || !productID) return;

        const product = productList.find((p) => p.id === toGlobalID(productID, 'Model'));
        if (!product) return;

        handleSetStatusClick(product);

        setHasResumed(true);

        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, productList, hasResumed, navigate]);

    const refetchProducts = () => {
        refetch(
            {
                search: search,
                salesChannelsIDs: salesChannelsIDs,
                productProvidersIDs: productProvidersIDs,
                cursor: '1',
                count: count,
                orderBy: orderBy || 'datedesc',
                productStatusID: productStatusID
            },
            { fetchPolicy: 'network-only' }
        );
        resetSelection();
    };

    const handleSetStatusClick = (product: Model, options?: { publishToStandaloneSite?: boolean }) => {
        if (!isAllowed(['PRODUCTS_PUBLISH'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        // A draft (incomplete) product cannot be published; it must be completed first. Unpublishing stays allowed.
        if (product.statusID !== 1 && isDraftProduct(product.configurationStatus)) {
            showErrorNotification({ message: T._('Complete the product configuration before publishing it.', 'Admin') });
            return;
        }

        const pricingVersion = subscriptionData.subscription?.maxProducts.pricingVersion ?? 'VERSION_1';
        if (pricingVersion === 'VERSION_2') {
            const numCurrentPublishedProducts = subscriptionData.subscription?.maxProducts.numPublishedProducts ?? 0;
            const numMaxPublishedProducts = subscriptionData.subscription?.maxProducts.maxProducts ?? 0;

            if (product.statusID === 2 && numCurrentPublishedProducts >= numMaxPublishedProducts) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        marketPlace={sellerMarketplace ?? undefined}
                        message={
                            <>
                                {stringFormat(
                                    T._(
                                        `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                        'Admin'
                                    ),
                                    numMaxPublishedProducts.toString()
                                )}
                            </>
                        }
                        onUpgradeCompleted={async () => {
                            console.log('upgrade completato, continuo con la pubblicazione');
                            setStatusProduct({
                                variables: {
                                    input: {
                                        productID: product.id,
                                        status: product.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                                    }
                                },
                                onError: (error) => {
                                    showErrorNotification({
                                        message: product.statusID === 1 ? T._('Failed Unpublish 1 product', 'Admin') : T._('Failed publish 1 product', 'Admin')
                                    });
                                },
                                onCompleted: (response) => {
                                    if (response.setStatusProduct && response.setStatusProduct.errors && response.setStatusProduct.errors.length > 0) {
                                        const wixError = response.setStatusProduct.errors.find((e) =>
                                            e.message?.includes('The product do not have a customization token')
                                        );

                                        if (wixError) {
                                            showDialog('wix-publish-error', <MessageDialog message={<WixPublishErrorMessage></WixPublishErrorMessage>} />);
                                            return;
                                        }
                                    }

                                    const numCurrentPublishedProducts = subscriptionData.subscription?.maxProducts.numPublishedProducts ?? 0;
                                    setIsFetchingUpdatedPublishedProductsList(true);

                                    const intervalId = setInterval(() => {
                                        fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {})
                                            .toPromise()
                                            .then((data) => {
                                                const newNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0;
                                                if (newNumPublished !== numCurrentPublishedProducts) {
                                                    console.log('blocco il pool, valore aggiornato', newNumPublished);
                                                    clearInterval(intervalId);
                                                    setIsFetchingUpdatedPublishedProductsList(false);
                                                    if (!selectedProductId) {
                                                        showSuccessNotification({
                                                            message:
                                                                product.statusID === 1
                                                                    ? T._('1 Product unpublished', 'Admin')
                                                                    : T._('1 Product published', 'Admin')
                                                        });
                                                    }
                                                    startTransition(() => {
                                                        refetchSubscription({}, { fetchPolicy: 'network-only' });
                                                    });
                                                }
                                            })
                                            .catch(() => {
                                                clearInterval(intervalId);
                                                setIsFetchingUpdatedPublishedProductsList(false);
                                            });
                                    }, 3000);
                                }
                            });
                        }}
                    />
                );
                return;
            }
        }

        const isPublishingProduct = product.statusID !== 1;
        const shouldPublishToStandaloneSite = options?.publishToStandaloneSite === true && isPublishingProduct && canPublishProductToStandaloneSite(product);

        if (
            !shouldPublishToStandaloneSite &&
            isPublishingProduct &&
            product?.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
            salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE') &&
            product.additionalCodes.length == 0 &&
            product.modelColorMappings.length == 0 &&
            product?.statusID === 2
        ) {
            const realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');

            showDialog(
                'connectProductDialog',
                <Suspense
                    fallback={
                        <DialogOverlayConnect>
                            <Loader />
                        </DialogOverlayConnect>
                    }
                >
                    <ConnectModelDialog
                        queryKey={Math.random().toString()}
                        productID={product.id}
                        merchantProductOptionsConnected={[]}
                        forPublishingProduct={true}
                        additionalCodes={product.additionalCodes.map((c) => {
                            return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName ?? '' };
                        })}
                        productColors={product.colors as ProductColor[]}
                        salesChannels={realSalesChannels}
                        productSalesChannelID={product.salesChannel.id}
                        onConfirmMappingVariants={() => {
                            closeDialog('connectProductDialog');
                            setStatusProduct({
                                variables: {
                                    input: {
                                        productID: product.id,
                                        status: product.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                                    }
                                },
                                onError: (error) => {
                                    showErrorNotification({
                                        message: product.statusID === 1 ? T._('Failed Unpublish 1 product', 'Admin') : T._('Failed publish 1 product', 'Admin')
                                    });
                                },
                                onCompleted: (response) => {
                                    if (response.setStatusProduct && response.setStatusProduct.errors && response.setStatusProduct.errors.length > 0) {
                                        const wixError = response.setStatusProduct.errors.find((e) =>
                                            e.message?.includes('The product do not have a customization token')
                                        );

                                        if (wixError) {
                                            showDialog('wix-publish-error', <MessageDialog message={<WixPublishErrorMessage></WixPublishErrorMessage>} />);
                                            return;
                                        }
                                    }

                                    refetchProducts();

                                    const numCurrentPublishedProducts = subscriptionData.subscription?.maxProducts.numPublishedProducts ?? 0;
                                    setIsFetchingUpdatedPublishedProductsList(true);

                                    const intervalId = setInterval(() => {
                                        fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {})
                                            .toPromise()
                                            .then((data) => {
                                                const newNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0;
                                                if (newNumPublished !== numCurrentPublishedProducts) {
                                                    console.log('blocco il pool, valore aggiornato', newNumPublished);
                                                    clearInterval(intervalId);
                                                    setIsFetchingUpdatedPublishedProductsList(false);
                                                    if (!selectedProductId) {
                                                        showSuccessNotification({
                                                            message:
                                                                product.statusID === 1
                                                                    ? T._('1 Product unpublished', 'Admin')
                                                                    : T._('1 Product published', 'Admin')
                                                        });
                                                    }
                                                    startTransition(() => {
                                                        refetchSubscription({}, { fetchPolicy: 'network-only' });
                                                    });
                                                }
                                            })
                                            .catch(() => {
                                                clearInterval(intervalId);
                                                setIsFetchingUpdatedPublishedProductsList(false);
                                            });
                                    }, 3000);
                                }
                            });
                        }}
                        onConfirm={async (sourceProduct, salesChannelId) => {
                            if (!product) {
                                showDialog('no-selection', <MessageDialog message={T._('Select a product', 'Admin')} />);
                                return;
                            }

                            if (!sourceProduct) return;

                            closeDialog('connectProductDialog');

                            const loadingNotification = showNotification({
                                type: 'default',
                                message: T._('Publishing your customizable product...', 'Admin')
                            });

                            let response!: productListConnectModelMutation$data;

                            try {
                                // Save product
                                response = await new Promise((resolve, reject) =>
                                    commitConnectModel({
                                        onCompleted: (response) => resolve(response),
                                        onError: (response) => reject(response),
                                        updater: (store) => {
                                            store.invalidateStore();
                                        },
                                        variables: {
                                            id: product.id,
                                            target: {
                                                name: sourceProduct!.name,
                                                sku: sourceProduct!.sku,
                                                imageUrl: sourceProduct!.thumbnail,
                                                salesChannelId
                                            }
                                        }
                                    })
                                );

                                showNotification({
                                    type: 'success',
                                    message: T._('Product connected and published!', 'Admin'),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });

                                refetchProducts();
                            } catch (ex: any) {
                                showNotification({
                                    type: 'danger',
                                    message: (
                                        <>
                                            {T._('Failed connecting and publishing product.', 'Admin')}
                                            <br />
                                            {ex.errors?.[0].extensions?.message}
                                        </>
                                    ),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                            }
                            removeNotification(loadingNotification);
                        }}
                    />
                </Suspense>
            );

            return;
        } else if (!shouldPublishToStandaloneSite && isPublishingProduct && !salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    productId={product.modelID}
                    redirectPage={'/products'}
                    subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                    message={
                        <label>
                            {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                        </label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        }

        if (!selectedProductId) {
            showInfoNotification({ message: product.statusID === 1 ? T._('Unpublishing 1 product', 'Admin') : T._('Publishing 1 product', 'Admin') });
        }
        setStatusProduct({
            variables: {
                input: {
                    productID: product.id,
                    status: product.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                }
            },
            onError: (error) => {
                showErrorNotification({
                    message: product.statusID === 1 ? T._('Failed Unpublish 1 product', 'Admin') : T._('Failed publish 1 product', 'Admin')
                });
            },
            onCompleted: (response) => {
                if (response.setStatusProduct && response.setStatusProduct.errors && response.setStatusProduct.errors.length > 0) {
                    const wixError = response.setStatusProduct.errors.find((e) => e.message?.includes('The product do not have a customization token'));

                    if (wixError) {
                        showDialog('wix-publish-error', <MessageDialog message={<WixPublishErrorMessage></WixPublishErrorMessage>} />);
                        return;
                    }
                }

                const numCurrentPublishedProducts = subscriptionData.subscription?.maxProducts.numPublishedProducts ?? 0;
                setIsFetchingUpdatedPublishedProductsList(true);

                const intervalId = setInterval(() => {
                    fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {})
                        .toPromise()
                        .then((data) => {
                            const newNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0;
                            if (newNumPublished !== numCurrentPublishedProducts) {
                                console.log('blocco il pool, valore aggiornato', newNumPublished);
                                clearInterval(intervalId);
                                setIsFetchingUpdatedPublishedProductsList(false);
                                if (!selectedProductId) {
                                    showSuccessNotification({
                                        message: product.statusID === 1 ? T._('1 Product unpublished', 'Admin') : T._('1 Product published', 'Admin')
                                    });
                                }
                                maybeShowShopifyDuplicationWarning(
                                    product.statusID !== 1 && product.salesChannel.ecommerceSettings?.ecommerceName === 'SHOPIFY',
                                    numCurrentPublishedProducts === 0 && newNumPublished > 0
                                );
                                startTransition(() => {
                                    refetchSubscription({}, { fetchPolicy: 'network-only' });
                                });
                            }
                        })
                        .catch(() => {
                            clearInterval(intervalId);
                            setIsFetchingUpdatedPublishedProductsList(false);
                        });
                }, 3000);
            }
        });
    };

    const handleDeleteClick = (product: Model) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to delete products', 'Admin') });
            return;
        }

        showDialog(
            'delete-product',
            <QuestionDialog
                destructive
                message={stringFormat(T._('Do you want delete the product "{0}"?', 'Admin'), product.name)}
                onYesClick={() => {
                    closeDialog('delete-product');

                    deleteProduct({
                        variables: {
                            connections: data?.models?.__id ? [data.models.__id] : [],
                            productID: product.id
                        },
                        onError: () => {
                            showErrorNotification({ message: T._('Failed deleting product', 'Admin') });
                        },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Product deleted.', 'Admin') });
                            refetchSubscription({}, { fetchPolicy: 'network-only' });
                            resetSelection();
                            if (productList.length <= 1) {
                                handleEmptyList(true);
                            }
                        }
                    });
                }}
            />
        );
    };

    const handleDeleteDirect = (product: Model) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to delete products', 'Admin') });
            return;
        }

        deleteProduct({
            variables: {
                connections: data?.models?.__id ? [data.models.__id] : [],
                productID: product.id
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed deleting product', 'Admin') });
            },
            onCompleted: () => {
                showSuccessNotification({ message: T._('Product deleted.', 'Admin') });
                refetchSubscription({}, { fetchPolicy: 'network-only' });
                resetSelection();
                if (productList.length <= 1) {
                    handleEmptyList(true);
                }
            }
        });
    };

    const handleDuplicate = (product: Model) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        if (subscriptionData.subscription?.maxProducts.canAddProduct === false) {
            showPlanUpgradeDialog(
                stringFormat(T._('You can configure max {0} products', 'Admin'), (subscriptionData.subscription?.maxProducts.maxProducts || 0).toString())
            );
            return;
        }

        if (product?.salesChannel?.ecommerceSettings?.ecommerceName === 'API' && !product?.salesChannel?.ecommerceSettings?.hasProductsApi) {
            showDialog(
                'no-api-products',
                <MessageDialog
                    message={
                        <p>
                            {T._('For duplicate the product you have to implement the ', 'Admin')}{' '}
                            <Link href={'https://zakeke.zendesk.com/hc/en-us/articles/360010310554-Products-Sync-V3'} target='_blank'>
                                {T._('product sync API', 'Admin')}
                            </Link>
                        </p>
                    }
                />
            );
            return;
        }
        showDialog(
            'duplicate',
            <DuplicateDialog
                sourceProductCode={product.code}
                salesChannelID={product.salesChannel.id}
                onConfirm={async (targets) => {
                    closeDialog('duplicate');

                    showDialog(
                        'duplicate-options',
                        <DuplicateOptionsDialog
                            dialogId='duplicate-options'
                            onConfirm={async (options) => {
                                const loadingNotification = showNotification({
                                    type: 'default',
                                    message: T._('Starting product duplication task...', 'Admin')
                                });

                                try {
                                    const destinationProducts = targets
                                        .map((x) => {
                                            const id = toLocalID(x.id);
                                            if (id == null) return null;
                                            return {
                                                id,
                                                sku: x.sku ?? null,
                                                name: x.name ?? null,
                                                imageUrl: x.thumbnail ?? null,
                                                statusId: null
                                            };
                                        })
                                        .filter((d): d is NonNullable<typeof d> => d !== null);
                                    const destinationProductIds = destinationProducts.map((d) => d.id);
                                    const salesChannelId = toLocalID(product.salesChannel?.id);

                                    await new Promise<productDuplicationTaskQueriesCreateMutation['response']>((resolve, reject) =>
                                        commitDuplicate({
                                            onCompleted: (response) => resolve(response),
                                            onError: (response) => reject(response),
                                            updater: (store) => {
                                                store.invalidateStore();
                                            },
                                            variables: {
                                                request: {
                                                    sourceProductId: product.modelID,
                                                    destinationProductIds,
                                                    destinationProducts,
                                                    cloneTemplates: options.cloneTemplates,
                                                    assignPricingRules: options.assignPricingRules,
                                                    salesChannelId
                                                }
                                            }
                                        })
                                    );
                                    showNotification({
                                        type: 'success',
                                        message: T._(
                                            'Duplication task started — you can safely leave this page, it won’t be interrupted. Come back anytime from Customizable Products > Duplication Tasks to check the progress.',
                                            'Admin'
                                        ),
                                        dismiss: {
                                            duration: 6000
                                        }
                                    });
                                    onDuplicationTaskCreated?.();
                                    navigate(backofficeUrl('/products?tab=tasks'));
                                } catch (ex: any) {
                                    console.error(ex);
                                    showNotification({
                                        type: 'danger',
                                        message: (
                                            <>
                                                {T._('Failed to start duplication task.', 'Admin')}
                                                <br />
                                                {ex.errors?.[0].extensions?.message}
                                            </>
                                        ),
                                        dismiss: {
                                            duration: 3000
                                        }
                                    });
                                }
                                resetSelection();
                                removeNotification(loadingNotification);
                            }}
                        />
                    );
                }}
            />
        );
    };

    const handleSetStatusMassiveClick = async (status: ProductStatus) => {
        if (!isAllowed(['PRODUCTS_PUBLISH'])) {
            showErrorNotification({ message: T._('You do not have permission to publish or unpublish products.', 'Admin') });
            return;
        }

        // Draft (incomplete) products cannot be published: exclude them from the publish batch and warn about it.
        if (status === 'PUBLISHED') {
            const draftCount = selectedData.filter((p) => p.statusID !== 1 && isDraftProduct(p.configurationStatus)).length;
            if (draftCount > 0) {
                showInfoNotification({
                    message: stringFormat(
                        T._('{0} draft product(s) were skipped: complete their configuration before publishing them.', 'Admin'),
                        draftCount.toString()
                    )
                });
            }
        }

        const productsToPublish = selectedData
            .filter((p) => p.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || p.additionalCodes.length > 0)
            .filter((p) => status !== 'PUBLISHED' || !isDraftProduct(p.configurationStatus))
            .map((el) => el.id);
        // Filtra e ordina i prodotti da pubblicare , dividendo in due categorie: già pubblicati e non pubblicati
        const productsToPublishPublished = selectedData
            .filter((p) => (p.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || p.additionalCodes.length > 0) && p.statusID === 1)
            .map((el) => el.id);

        const productsToPublishUnpublished = selectedData
            .filter((p) => (p.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || p.additionalCodes.length > 0) && p.statusID === 2)
            .map((el) => el.id);

        await publishProducts(productsToPublish, status);
    };

    const publishProducts = async (productsToPublish: string[], status: ProductStatus) => {
        const pricingVersion = subscriptionData.subscription?.maxProducts.pricingVersion ?? 'VERSION_1';

        // Filtra e ordina i prodotti da pubblicare , dividendo in due categorie: già pubblicati e non pubblicati
        const productsToPublishPublished = selectedData
            .filter((p) => (p.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || p.additionalCodes.length > 0) && p.statusID === 1)
            .map((el) => el.id);

        const productsToPublishUnpublished = selectedData
            .filter(
                (p) =>
                    (p.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || p.additionalCodes.length > 0) &&
                    p.statusID !== 1 &&
                    (status !== 'PUBLISHED' || !isDraftProduct(p.configurationStatus))
            )
            .map((el) => el.id);

        let numCurrentPublishedProducts = subscriptionData.subscription?.maxProducts.numPublishedProducts ?? 0; // 0

        const data = await fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {}).toPromise();
        let updatedNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0; // 0
        let maxProducts = data?.subscription?.maxProducts?.maxProducts; // 50
        const hadPublishedProductsBeforePublish = updatedNumPublished > 0;

        const availableSlots = pricingVersion === 'VERSION_2' && maxProducts ? maxProducts - updatedNumPublished : productsToPublish.length;
        const publishableProducts = productsToPublishUnpublished.slice(0, availableSlots); // 2
        const nonPublishableProducts = productsToPublishUnpublished.slice(availableSlots); // 0

        if (status === 'PUBLISHED') {
            // caso 2: hai un numero di prodotti pubblicabili inferiori rispetto a quelli che vuoi pubblicare, divido quindi in due array
            // e avviso che la seconda parte dei prodotti non è stata pubblicata ma può esserlo dopo un upgrade piano
            let productsText = nonPublishableProducts.length === 1 ? 'product' : 'products';

            const infoId = showInfoNotification({
                message: stringFormat(
                    T._('{0} {1} {2}', 'Admin'),
                    status === 'PUBLISHED' ? T._('publishing', 'Admin') : T._('unpublishing', 'Admin'),
                    productsToPublish.length.toString(),
                    productsText
                )
            });

            setStatusProductMassive({
                variables: {
                    // Allora, qui dobbiamo pubblicare tutti i prodotti unpublished che rientrano nel limite (es. di 4 prodotti ne pubblico 2), ma tutti
                    // i prodotti già pubblished, basta ripubblicarli (per fare update ecommerce)
                    productIDs: publishableProducts.concat(productsToPublishPublished),
                    status: status
                },
                onError: () => {
                    removeNotification(infoId);

                    showErrorNotification({
                        message: stringFormat(
                            T._('Failed {0} {1} {2} ', 'Admin'),
                            status === 'PUBLISHED' ? T._('publishing', 'Admin') : T._('unpublishing', 'Admin'),
                            nonPublishableProducts.length.toString(),
                            productsText
                        )
                    });
                },

                onCompleted: () => {
                    removeNotification(infoId);

                    setIsFetchingUpdatedPublishedProductsList(true);
                    let updatedNumPublished = numCurrentPublishedProducts + publishableProducts.length;

                    // Vari casi

                    // 1. Se sono riuscito a pubblicare tutti i prodotti (es. solo prodotti published, oppure published + unpublished ma è tutto ok)
                    if (publishableProducts.length + productsToPublishPublished.length > 0) {
                        showSuccessNotification({
                            message: stringFormat(
                                T._('{0} {1} {2} ', 'Admin'),
                                (publishableProducts.length + productsToPublishPublished.length).toString(),
                                productsText,
                                status === 'PUBLISHED' ? T._('published', 'Admin') : T._('unpublished', 'Admin')
                            )
                        });
                        maybeShowShopifyDuplicationWarning(
                            selectedData.some(
                                (product) =>
                                    publishableProducts.includes(product.id) &&
                                    product.statusID !== 1 &&
                                    product.salesChannel.ecommerceSettings?.ecommerceName === 'SHOPIFY'
                            ),
                            !hadPublishedProductsBeforePublish
                        );
                        setIsFetchingUpdatedPublishedProductsList(false);
                    }

                    if (nonPublishableProducts.length > 0) {
                        showErrorNotification({
                            message: stringFormat(
                                T._('Failed {0} {1} {2} ', 'Admin'),
                                status === 'PUBLISHED' ? T._('publishing', 'Admin') : T._('unpublishing', 'Admin'),
                                nonPublishableProducts.length.toString(),
                                productsText
                            )
                        });
                    }

                    if (publishableProducts.length === 0 && productsToPublishPublished.length === 0) {
                        showDialog(
                            'Upgrade_Plan',
                            <UpgradeDialog
                                redirectUrlFromDashboard={encodeURI(window.location.href)}
                                marketPlace={sellerMarketplace ?? undefined}
                                message={
                                    <>
                                        {stringFormat(
                                            T._(
                                                `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                                'Admin'
                                            ),
                                            maxProducts!.toString()
                                        )}
                                    </>
                                }
                                onUpgradeCompleted={async () => {
                                    publishProducts(productsToPublishUnpublished, status);
                                }}
                            />
                        );
                    } else if (nonPublishableProducts.length > 0) {
                        showDialog(
                            'Upgrade_Plan',
                            <UpgradeDialog
                                redirectUrlFromDashboard={encodeURI(window.location.href)}
                                marketPlace={sellerMarketplace ?? undefined}
                                message={
                                    <>
                                        {stringFormat(
                                            T._(
                                                `Your current plan allows for {0} published products. We've published {1} products, but {2} items couldn't be published as they exceed your product limit. Upgrade your plan to increase your published product slots.`,
                                                'Admin'
                                            ),
                                            maxProducts!.toString(),
                                            (publishableProducts.length + productsToPublishPublished.length).toString(),
                                            nonPublishableProducts.length.toString()
                                        )}
                                    </>
                                }
                                onUpgradeCompleted={async () => {
                                    publishProducts(productsToPublishUnpublished, status);
                                }}
                            />
                        );
                    }

                    if (publishableProducts.length === 0) {
                        setIsFetchingUpdatedPublishedProductsList(false);
                        return;
                    }

                    const intervalId = setInterval(async () => {
                        try {
                            const data = await fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {}).toPromise();
                            updatedNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0;

                            if (updatedNumPublished !== numCurrentPublishedProducts) {
                                clearInterval(intervalId);
                                numCurrentPublishedProducts = updatedNumPublished;
                            }
                        } catch {
                            clearInterval(intervalId);
                            setIsFetchingUpdatedPublishedProductsList(false);
                        }
                    }, 3000);
                }
            });
        }
        // sono unpublish, posso procedere senza check
        else {
            // caso 3: la pubblicazione può procedere in quanto il limite di prodotti pubblicati non è stato raggiunto
            let publishingOrUnpublishingProducts = 'unpublishing';
            let numPublishingOrUnpublishingProducts = productsToPublish.length;
            let productsText = numPublishingOrUnpublishingProducts === 1 ? 'product' : 'products';

            showInfoNotification({
                message: stringFormat(
                    T._('{0} {1} {2}', 'Admin'),
                    publishingOrUnpublishingProducts,
                    numPublishingOrUnpublishingProducts.toString(),
                    productsText
                )
            });

            setStatusProductMassive({
                variables: {
                    productIDs: productsToPublish,
                    status: status
                },
                onError: () => {
                    showErrorNotification({
                        message: stringFormat(
                            T._('Failed {0} {1} {2} ', 'Admin'),
                            numPublishingOrUnpublishingProducts.toString(),
                            productsText,
                            publishingOrUnpublishingProducts
                        )
                    });
                },

                onCompleted: () => {
                    setIsFetchingUpdatedPublishedProductsList(false);
                    setIsFetchingUpdatedPublishedProductsList(true);
                    let updatedNumPublished = numCurrentPublishedProducts;

                    const intervalId = setInterval(async () => {
                        try {
                            const data = await fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {}).toPromise();
                            updatedNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0;

                            if (updatedNumPublished !== numCurrentPublishedProducts) {
                                console.log('blocco il pool, valore aggiornato', updatedNumPublished);
                                clearInterval(intervalId);
                                numCurrentPublishedProducts = updatedNumPublished;
                                setIsFetchingUpdatedPublishedProductsList(false);
                                showSuccessNotification({
                                    message: stringFormat(
                                        T._('{0} {1} {2} ', 'Admin'),
                                        numPublishingOrUnpublishingProducts.toString(),
                                        productsText,
                                        'unpublished'
                                    )
                                });
                            }
                        } catch {
                            clearInterval(intervalId);
                            setIsFetchingUpdatedPublishedProductsList(false);
                        }
                    }, 3000);
                }
            });
        }
    };

    const handleOnlickMassiveDelete = () => {
        if (!isAllowed(['PRODUCTS_DELETE'])) {
            showErrorNotification({ message: T._('You do not have permission to delete products.', 'Admin') });
            return;
        }

        showDialog(
            'delete-products',
            <QuestionDialog
                destructive
                message={stringFormat(T._('Do you want delete {0} products?', 'Admin'), selectedData.length.toString())}
                onYesClick={() => {
                    closeDialog('delete-products');
                    // ci vuole lo scroll on top
                    deleteProductMassive({
                        variables: {
                            connections: data?.models?.__id ? [data.models.__id] : [],
                            productIDs: selectedData.map((el) => el.id)
                        },
                        onError: () => {
                            showErrorNotification({ message: T._('Failed deleting products', 'Admin') });
                        },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Products deleted.', 'Admin') });
                            resetSelection();
                            refetchSubscription({}, { fetchPolicy: 'network-only' });
                            resetSelection();
                            if ((subscriptionData.subscription?.maxProducts.numProducts ?? 0) == 0) {
                                handleEmptyList(true);
                            }
                        }
                    });
                }}
            />
        );
    };

    const handleAddSalesChannels = (product: Model) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        // A draft (incomplete) product cannot be connected to a sales channel until its configuration is completed.
        if (isDraftProduct(product.configurationStatus)) {
            showErrorNotification({ message: T._('Complete the product configuration before connecting it to a sales channel.', 'Admin') });
            return;
        }

        let realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');

        // if (product.sourceProductTemplateID) {
        if (realSalesChannels.length == 0) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                    message={
                        <label>
                            {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                        </label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        } else if (realSalesChannels.length == 1 && !canAddSalesChannelFromPublishedMiniSite(product)) {
            isShopifyTestStore
                ? showDialog(
                      'add-sales-channel-not-available',
                      <MessageDialog
                          windowDecorator={SalesChannelNotAvailableDialogWindow}
                          message={
                              <>
                                  {T._(
                                      "We're sorry, but you cannot add sales channels at this time. Your account is currently in trial mode on Shopify or is a development store.",
                                      'Admin'
                                  )}
                                  <br />
                                  <br />
                                  {T._('To access this feature, please upgrade to an active Shopify plan.', 'Admin')}
                                  <br />
                                  <br />
                                  {T._('Thank you for your understanding.', 'Admin')}
                              </>
                          }
                          buttons={[
                              {
                                  label: T._('OK', 'Admin'),
                                  onClick: () => {
                                      closeDialog('add-sales-channel-not-available');
                                  }
                              }
                          ]}
                      />
                  )
                : showDialog(
                      'add-sales-channel',
                      <AddSalesChannelDialog
                          onSalesChannelCreated={() => {
                              closeDialog('add-sales-channel');
                              onSalesChannelCreated();
                          }}
                      ></AddSalesChannelDialog>
                  );
        } else {
            let availableSalesChannels = realSalesChannels.filter(
                (rs) => rs.id != product.salesChannel.id && !product.additionalCodes.some((ac) => ac.salesChannelID == rs.id)
            );

            if (availableSalesChannels.length == 0) {
                showNotification({
                    type: 'danger',
                    message: T._('The product has been connected with all sales channels.', 'Admin'),
                    dismiss: {
                        duration: 3000
                    }
                });
                return;
            }

            showDialog(
                'connectProductDialog',
                <Suspense
                    fallback={
                        <DialogOverlayConnect>
                            <Loader />
                        </DialogOverlayConnect>
                    }
                >
                    <ConnectModelDialog
                        productID={'guid://Zakeke/' + uuid() + '/' + toLocalID(product.id)}
                        forEditProduct={false}
                        forAddingNewSalesChannel={true}
                        merchantProductOptionsConnected={[]}
                        additionalCodes={product.additionalCodes.map((c) => {
                            return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName ?? '' };
                        })}
                        productColors={product.colors as ProductColor[]}
                        salesChannels={availableSalesChannels}
                        productSalesChannelID={product.salesChannel.id}
                        onConfirm={async (sourceProduct, salesChannelId) => {
                            if (!product) {
                                showDialog('no-selection', <MessageDialog message={T._('Select a product', 'Admin')} />);
                                return;
                            }

                            if (!sourceProduct) return;

                            closeDialog('connectProductDialog');

                            const loadingNotification = showNotification({
                                type: 'default',
                                message: T._('Publishing your customizable product...', 'Admin')
                            });

                            let response!: productListConnectModelMutation$data;

                            try {
                                // Save product
                                response = await new Promise((resolve, reject) =>
                                    commitConnectModel({
                                        onCompleted: (response) => resolve(response),
                                        onError: (response) => reject(response),
                                        updater: (store) => {
                                            store.invalidateStore();
                                        },
                                        variables: {
                                            id: product.id,
                                            target: {
                                                name: sourceProduct!.name,
                                                sku: sourceProduct!.sku,
                                                imageUrl: sourceProduct!.thumbnail,
                                                salesChannelId
                                            }
                                        }
                                    })
                                );

                                showNotification({
                                    type: 'success',
                                    message: T._('Product connected and published!', 'Admin'),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });

                                refetchProducts();
                            } catch (ex: any) {
                                showNotification({
                                    type: 'danger',
                                    message: (
                                        <>
                                            {T._('Failed connecting and publishing product.', 'Admin')}
                                            <br />
                                            {ex.errors?.[0].extensions?.message}
                                        </>
                                    ),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                            }
                            removeNotification(loadingNotification);
                        }}
                    />
                </Suspense>
            );
        }
    };

    const handleDisconnectSalesChannel = (product: Model) => {
        showDialog(
            'delete-channel',
            <DisconnectProductSalesChannel
                productID={product.id}
                sku={product.additionalCodes[0].code}
                salesChannelID={product.additionalCodes[0].salesChannelID}
                onRemoveCompleted={() => {
                    refetchProducts();
                }}
            ></DisconnectProductSalesChannel>
        );
    };

    const handleMouseCickTest = (product: Model) => {
        // A draft (incomplete) product cannot be previewed until its configuration is completed.
        if (isDraftProduct(product.configurationStatus)) {
            showErrorNotification({ message: T._('Complete the product configuration before previewing it.', 'Admin') });
            return;
        }

        if (!isAllowed(['ORDERS'])) {
            showErrorNotification({ message: T._('You must have the ability to also access orders to test a product and create a test order.', 'Admin') });
            return;
        }

        let linkTest = '/test-mode/' + toLocalID(product.id);
        navigate(backofficeUrl(linkTest));
    };

    const handlePrototypeClick = (product: Model) => {
        let linkTest = '/test-mode/' + toLocalID(product.id) + '?isPrototype=1';
        navigate(backofficeUrl(linkTest));
    };

    const handleSyncWithEcommerceClick = (productIDs: string[]) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        syncWithEcommerce({
            variables: {
                productIDs: productIDs
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed syncing product', 'Admin') });
            },
            onCompleted: () => {
                showSuccessNotification({ message: T._('Product image/name synced', 'Admin') });
            }
        });
    };

    const handleMarketplacePreviewClick = (productID: string) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog('marketplace-preview-url', <MarketplacePreviewUrlDialog productID={productID} />);
    };

    const handleEdit = async (product: Model) => {
        const env = product.zakekeEnvironment as EnvType;

        console.log('handleEdit', product);
        dispatch({ type: 'LOAD_PRODUCT', model: product });

        const link = '/product?product_id=' + toLocalID(product.id);
        const linkViewer = '/' + config.culture + '/Admin/DAM?page=/gallery';
        const linkComposer = '/' + config.culture + '/Composer?page=product&id=' + toLocalID(product.id);

        console.log('🔧 handleEdit - Environment:', product.zakekeEnvironment, 'Feature flag active:', isNewConfiguratorBackoffice);

        // Logica basata su feature flag
        if (!isNewConfiguratorBackoffice) {
            // FF DISATTIVO: usa i link diretti senza settare lastUsedTool
            switch (product.zakekeEnvironment) {
                case 'VIEWER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF disabled, VIEWER -> linkViewer:', linkViewer);
                    window.location.href = linkViewer;
                    break;
                case 'COMPOSER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF disabled, COMPOSER -> linkComposer:', linkComposer);
                    window.location.href = linkComposer;
                    break;
                case 'CUSTOMIZER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF disabled, CUSTOMIZER -> backoffice link:', link);
                    navigate(backofficeUrl(link));
                    break;
                default:
                    navigate(backofficeUrl(link));
                    break;
            }
        } else {
            // FF ATTIVO: setta sempre il lastUsedTool appropriato
            console.log('product.zakekeEnvironment', product.zakekeEnvironment);
            switch (product.zakekeEnvironment) {
                case 'VIEWER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF active, VIEWER -> setting tool 3 + linkViewer:', linkViewer);
                    setLastUsedTool('VIEWER');
                    window.location.href = linkViewer;
                    break;
                case 'COMPOSER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF active, COMPOSER -> setting tool 1 + backoffice:', link);
                    setLastUsedTool('COMPOSER');
                    navigate(backofficeUrl(link));
                    break;
                case 'CUSTOMIZER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF active, CUSTOMIZER -> setting tool 1 + backoffice:', link);
                    setLastUsedTool('CUSTOMIZER');
                    navigate(backofficeUrl(link));
                    break;
                default:
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF active, DEFAULT -> backoffice:', link);
                    setLastUsedTool('CUSTOMIZER');
                    navigate(backofficeUrl(link));
                    break;
            }
        }
    };

    const handleUpdateVariants = async (product: Model) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        const productIsResizable = await isResizable(product.id);

        if (productIsResizable === null) {
            return;
        }

        if (productIsResizable) {
            showErrorNotification({ message: T._('You cannot add variants to a resizable product.', 'Admin') });
            return;
        }

        const link = '/product/variations/?product_id=' + toLocalID(product.id);
        navigate(backofficeUrl(link));
    };

    const sourceStores = getSourceStores(salesChannels);

    const headings = [
        [headingCheck, '45px'],
        ['', '85px'],
        [T._('Name', 'Admin'), '1.2fr'],
        [T._('Code', 'Admin'), '1fr'],
        [<LeftColContainer>{T._('Tool', 'Admin')}</LeftColContainer>, '0.4fr'],
        [<LeftColContainer>{T._('Status', 'Admin')}</LeftColContainer>, '0.6fr'],
        [<LeftColContainer>{T._('Source', 'Admin')}</LeftColContainer>, '0.6fr'],
        [<LeftColContainer>{T._('Channels', 'Admin')}</LeftColContainer>, '0.6fr'],
        <RightColContainer></RightColContainer>
    ] as TableHeading[];

    const isTableReplacingLoading = isInFightDelete || isInFightDeleteMassive || isInFightDuplicate || isInFightSync;

    const getBulkButtons = (): BulkButton[] => {
        let buttons: BulkButton[] = [];

        if (
            ecommerceName &&
            ecommerceName !== 'NONE' &&
            !selectedData.some((p) => p.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' && p.additionalCodes.length == 0)
        ) {
            buttons = [
                ...buttons,
                {
                    key: 'publish',
                    label: T._('Publish', 'Admin'),
                    tooltip: T._('Publish all selected products to your store', 'Admin'),
                    icon: <Eye />,
                    onClick: (e) => handleSetStatusMassiveClick('PUBLISHED'),
                    disabled: isInFightUpdateMassive || isFetchingUpdatedPublishedProductsList
                },
                {
                    key: 'unpublish',
                    label: T._('Unpublish', 'Admin'),
                    tooltip: T._('Remove all selected products from your store', 'Admin'),
                    icon: <EyeSlash />,
                    onClick: (e) => handleSetStatusMassiveClick('UN_PUBLISHED'),
                    disabled: isInFightUpdateMassive || isFetchingUpdatedPublishedProductsList
                }
            ];
        }

        if (
            (ecommerceName === 'SHOPIFY' || ecommerceName === 'WOO_COMMERCE' || ecommerceName === 'BIG_COMMERCE' || ecommerceName === 'MAGENTO') &&
            !selectedData.some((p) => p.salesChannel.ecommerceSettings?.ecommerceName === 'NONE')
        ) {
            buttons = [
                ...buttons,
                {
                    key: 'sync',
                    label: T._('Sync product image/name with e-commerce', 'Admin'),
                    tooltip: T._('Sync image/name with e-commerce', 'Admin'),
                    icon: <SyncIcon />,
                    onClick: (e) => handleSyncWithEcommerceClick(selectedData.map((el) => el.id)),
                    disabled: isInFightSync
                }
            ];
        }
        if (!selectedData.some((p) => p.zakekeEnvironment === 'VIEWER')) {
            buttons = [
                ...buttons,
                {
                    key: 'delete',
                    label: T._('Delete', 'Admin'),
                    tooltip: T._('Delete the product from your list', 'Admin'),
                    icon: <Trash />,
                    onClick: (e) => handleOnlickMassiveDelete(),
                    disabled: isInFightDeleteMassive || isInFightUpdateMassive
                }
            ];
        }
        return buttons;
    };

    return (
        <>
            {!isTableReplacingLoading && (
                <>
                    {selectedData.length > 0 && (
                        <BulkSelectionBar buttons={getBulkButtons()} selectionCount={selectedData.length} data-testid='bulk-actions-container'>
                            {isInFightUpdateMassive && <StyledSpinner />}
                        </BulkSelectionBar>
                    )}
                    {productList.length === 0 && search.length > 0 && (
                        <EmptyList
                            data-testid='empty-list-search-no-results'
                            icon={search.length > 0 ? <NotFoundIcon /> : null}
                            title={T._('No products found', 'Admin')}
                            subTitle={T._('Sorry, no product was found for your search. Please try using different keywords.', 'Admin')}
                        />
                    )}
                    {productList.length > 0 && (
                        <Table headings={headings} data-testid='products-table' compact $grayout={isPending}>
                            {mapRows(
                                visibleProductList,
                                (product: Model) => {
                                    const salesChannelTooltipItems = getSalesChannelTooltipItems(product);

                                    return [
                                        <Image url={product.smallImageUrl}></Image>,
                                        <NameCell
                                            name={product.name}
                                            id={product.modelID.toString()}
                                            env={product.zakekeEnvironment}
                                            onNameClick={() => {
                                                if (product.zakekeEnvironment === 'COMPOSER') {
                                                    window.location.href = `/Composer?id=${product.modelID}&page=product`;
                                                } else if (product.zakekeEnvironment === 'VIEWER') {
                                                    window.location.href = '/' + config.culture + '/Admin/DAM?page=/gallery';
                                                } else {
                                                    navigate(backofficeUrl('/product?product_id=' + product.modelID));
                                                }
                                            }}
                                        />,
                                        <ProductCodeCell code={product.code} modelID={product.modelID} />,
                                        <LeftColContainer data-testid={`product-tool-${product.modelID}`}>
                                            <ZTooltip
                                                position='top'
                                                title={
                                                    product.zakekeEnvironment === 'CUSTOMIZER'
                                                        ? 'Customizer'
                                                        : product.zakekeEnvironment === 'COMPOSER'
                                                          ? 'Configurator'
                                                          : 'Viewer'
                                                }
                                            >
                                                <TableToolIcon>
                                                    {product.zakekeEnvironment === 'CUSTOMIZER' ? (
                                                        <CustomizerIcon />
                                                    ) : product.zakekeEnvironment === 'COMPOSER' ? (
                                                        <ComposerIcon />
                                                    ) : (
                                                        <ViewerIcon />
                                                    )}
                                                </TableToolIcon>
                                            </ZTooltip>
                                        </LeftColContainer>,
                                        <LeftColContainer data-testid={`product-status-${product.modelID}`}>
                                            {product.configurationStatus === 'PARTIAL' ? (
                                                <Badge variant='warning'>{T._('Incomplete', 'Admin')}</Badge>
                                            ) : (
                                                <Badge variant={product.statusID === 1 ? 'success' : 'neutral'}>
                                                    {product.statusID === 1 ? T._('Online', 'Admin') : T._('Not published', 'Admin')}
                                                </Badge>
                                            )}
                                            {hasBrokenSalesChannel(product) && (
                                                <ZTooltip
                                                    position='top'
                                                    interactive={true}
                                                    html={
                                                        <span>
                                                            {T._(
                                                                'This product is no longer functioning on the websites where it is published because the originating sales channel has been removed. Please contact ',
                                                                'Admin'
                                                            )}
                                                            <Link href='mailto:help@zakeke.com'>help@zakeke.com</Link>
                                                            {T._(' for assistance.', 'Admin')}
                                                        </span>
                                                    }
                                                >
                                                    <BrokenBadge variant='danger-solid' data-testid={`product-broken-badge-${product.modelID}`}>
                                                        {T._('broken', 'Admin')}
                                                    </BrokenBadge>
                                                </ZTooltip>
                                            )}
                                        </LeftColContainer>,
                                        <LeftColContainer data-testid={`product-source-${product.modelID}`}>
                                            <SalesChannelCountTooltip channels={salesChannelTooltipItems}>
                                                <SourceCell>
                                                    {product.provider !== 'ZAKEKE' ? (
                                                        <Badge variant='pod'>{T._('POD', 'Admin')}</Badge>
                                                    ) : product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' ? (
                                                        <Badge variant='standalone'>{T._('Standalone', 'Admin')}</Badge>
                                                    ) : (
                                                        <Badge variant='store'>{T._('Store', 'Admin')}</Badge>
                                                    )}
                                                </SourceCell>
                                            </SalesChannelCountTooltip>
                                            {product.productProviderSyncState == 0 && (
                                                <ZTooltip
                                                    position='top'
                                                    interactive={true}
                                                    html={
                                                        <span>
                                                            {T._(
                                                                'Product is syncing. Refresh page to update. If after a few hours, the product is still not synced, read ',
                                                                'Admin'
                                                            )}
                                                            <Link
                                                                href='https://zakeke.zendesk.com/hc/en-us/articles/10982534777756-Why-my-Printful-products-are-unsynced'
                                                                target='_blank'
                                                            >
                                                                {T._('this article', 'Admin')}
                                                            </Link>
                                                            .
                                                        </span>
                                                    }
                                                >
                                                    <StyledSpinnerProviderSync />
                                                </ZTooltip>
                                            )}
                                        </LeftColContainer>,

                                        <SalesChannelColContainer data-testid={`product-sales-channels-${product.modelID}`}>
                                            {product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
                                                !product.additionalCodes.some((ac) => ac.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') &&
                                                T._('None', 'Admin')}
                                            {product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
                                                product.additionalCodes.length === 1 &&
                                                product.additionalCodes.length && (
                                                    <SalesChannelCountTooltip channels={salesChannelTooltipItems}>
                                                        <span data-testid={`sales-channels-single-additional-count-${product.modelID}`}>
                                                            {product.additionalCodes.length}
                                                        </span>
                                                    </SalesChannelCountTooltip>
                                                )}
                                            {product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' && product.additionalCodes.length > 1 && (
                                                <SalesChannelCountTooltip channels={salesChannelTooltipItems}>
                                                    <SalesChannelClickable
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            showDialog(
                                                                'product-sales-channels',
                                                                <ProductSalesChannelList
                                                                    additionalCodes={product.additionalCodes}
                                                                    code={product.code}
                                                                    productID={product.id}
                                                                    salesChannel={product.salesChannel}
                                                                />
                                                            );
                                                        }}
                                                        data-testid={`sales-channels-dropdown-${product.modelID}`}
                                                    >
                                                        <span data-testid={`sales-channels-count-${product.modelID}`}>{product.additionalCodes?.length}</span>
                                                        <WrapperAngleDown>
                                                            <AngleDown />
                                                        </WrapperAngleDown>
                                                    </SalesChannelClickable>
                                                </SalesChannelCountTooltip>
                                            )}
                                            {product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' && product.additionalCodes?.length === 0 && (
                                                <SalesChannelCountTooltip channels={salesChannelTooltipItems}>
                                                    <span data-testid={`sales-channels-single-${product.modelID}`}>{1}</span>
                                                </SalesChannelCountTooltip>
                                            )}
                                            {product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' && product.additionalCodes?.length > 0 && (
                                                <SalesChannelCountTooltip channels={salesChannelTooltipItems}>
                                                    <SalesChannelClickable
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            showDialog(
                                                                'product-sales-channels',
                                                                <ProductSalesChannelList
                                                                    additionalCodes={product.additionalCodes}
                                                                    code={product.code}
                                                                    productID={product.id}
                                                                    salesChannel={product.salesChannel}
                                                                />
                                                            );
                                                        }}
                                                        data-testid={`sales-channels-multiple-dropdown-${product.modelID}`}
                                                    >
                                                        <span data-testid={`sales-channels-multiple-count-${product.modelID}`}>
                                                            {product.additionalCodes?.length + 1}
                                                        </span>
                                                        <WrapperAngleDown>
                                                            <AngleDown />
                                                        </WrapperAngleDown>
                                                    </SalesChannelClickable>
                                                </SalesChannelCountTooltip>
                                            )}
                                        </SalesChannelColContainer>,

                                        <ActionsColContainer data-testid={`product-actions-${product.modelID}`}>
                                            {(product.zakekeEnvironment === 'CUSTOMIZER' || product.zakekeEnvironment === 'COMPOSER') &&
                                                !isPrototypeModeEnabled &&
                                                !isDraftProduct(product.configurationStatus) && (
                                                    <RowActionBtn
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMouseCickTest(product);
                                                        }}
                                                        aria-label={T._('Test product', 'Admin')}
                                                        data-testid={`preview-button-${product.modelID}`}
                                                    >
                                                        <ProductRowActionIcon aria-hidden='true'>
                                                            <MaterialVisibilitySvg />
                                                        </ProductRowActionIcon>
                                                    </RowActionBtn>
                                                )}
                                            {product.zakekeEnvironment === 'CUSTOMIZER' && isPrototypeModeEnabled && (
                                                <TextButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePrototypeClick(product);
                                                    }}
                                                    data-testid={`prototype-button-${product.modelID}`}
                                                >
                                                    {T._('Prototype', 'Admin')}
                                                </TextButton>
                                            )}
                                            <ProductActionsDropdown
                                                key={'selectAction_' + product.modelID}
                                                product={product}
                                                salesChannels={salesChannels}
                                                isNewConfiguratorBackoffice={isNewConfiguratorBackoffice}
                                                isInFightUpdateMassive={isInFightUpdateMassive}
                                                isFetchingUpdatedPublishedProductsList={isFetchingUpdatedPublishedProductsList}
                                                canPublishToStandaloneSite={canPublishProductToStandaloneSite(product)}
                                                canAddSalesChannelFromPublishedMiniSite={canAddSalesChannelFromPublishedMiniSite(product)}
                                                onShowDetails={setSelectedProductId}
                                                onEdit={handleEdit}
                                                onSetStatus={handleSetStatusClick}
                                                onPublishToStandaloneSite={(product) => handleSetStatusClick(product, { publishToStandaloneSite: true })}
                                                onDuplicate={handleDuplicate}
                                                onAddSalesChannels={handleAddSalesChannels}
                                                onDisconnectSalesChannel={handleDisconnectSalesChannel}
                                                onUpdateVariants={handleUpdateVariants}
                                                onSyncWithEcommerce={handleSyncWithEcommerceClick}
                                                onMarketplacePreview={handleMarketplacePreviewClick}
                                                onDelete={handleDeleteClick}
                                            />
                                            <RowActionBtn
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedProductId(product.id);
                                                }}
                                                aria-label={T._('Open panel', 'Admin')}
                                                data-testid={`open-sidebar-button-${product.modelID}`}
                                            >
                                                <ProductRowActionIcon aria-hidden='true'>
                                                    <MaterialChevronRightSvg />
                                                </ProductRowActionIcon>
                                            </RowActionBtn>
                                        </ActionsColContainer>
                                    ];
                                },
                                (product: Model) => setSelectedProductId(product.id),
                                disableProviderProducts ? (product: Model) => DISABLED_PROVIDERS.has(product.provider) : undefined
                            )}
                            {hasNext && isLoadingNext && <ProductTableRowsSkeleton rows={4} />}
                        </Table>
                    )}
                </>
            )}
            {isTableReplacingLoading && <ProductTableSkeleton headings={headings} rows={8} />}
            {selectedProductId && (
                <ProductSidePanel
                    productId={selectedProductId}
                    canAddSalesChannelFromPublishedMiniSite={(() => {
                        const p = getSelectedProduct();
                        return p ? canAddSalesChannelFromPublishedMiniSite(p) : false;
                    })()}
                    onClose={() => setSelectedProductId(null)}
                    onDelete={() => {
                        const p = getSelectedProduct();
                        setSelectedProductId(null);
                        if (p) handleDeleteDirect(p);
                    }}
                    onPreview={() => {
                        const p = getSelectedProduct();
                        if (p) handleMouseCickTest(p);
                    }}
                    onPublishToggle={() => {
                        const p = getSelectedProduct();
                        if (p) {
                            handleSetStatusClick(p, {
                                publishToStandaloneSite:
                                    p.statusID !== 1 &&
                                    canPublishProductToStandaloneSite(p) &&
                                    !salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')
                            });
                        }
                    }}
                    onDuplicate={() => {
                        const p = getSelectedProduct();
                        setSelectedProductId(null);
                        if (p) handleDuplicate(p);
                    }}
                    onAddSalesChannel={() => {
                        const p = getSelectedProduct();
                        if (p) handleAddSalesChannels(p);
                    }}
                />
            )}
        </>
    );
};

export default ProductList;
